import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { PrimaryButton } from '../components/common/PrimaryButton';
import { SecondaryButton } from '../components/common/SecondaryButton';

interface OrgSummary {
  orgId: string;
  orgName: string;
  plan: string;
  status: string;
  activeAccounts: number;
  eventsThisMonth: number;
  accountLimit: number;
  eventLimit: number;
  role: string;
  createdAt: string;
}

const PLAN_COLORS: Record<string, string> = {
  free: '#9e9e9e',
  starter: '#2196f3',
  growth: '#4caf50',
  pro: '#ff9800',
  enterprise: '#9c27b0',
};

export const AdminPage: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editPlan, setEditPlan] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      setLoading(true);
      const data = await api.adminListOrgs();
      setOrgs(data.orgs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (orgId: string) => {
    try {
      setDetailLoading(true);
      const data = await api.adminGetOrg(orgId);
      setSelectedOrg(data);
      setEditPlan(data.billing?.plan || 'free');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load org details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedOrg) return;
    try {
      setSaving(true);
      await api.adminUpdateOrg(selectedOrg.orgId, { plan: editPlan });
      setSelectedOrg(null);
      loadOrgs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const filteredOrgs = orgs.filter((org) => {
    const matchesSearch = !search ||
      org.orgName.toLowerCase().includes(search.toLowerCase()) ||
      org.orgId.toLowerCase().includes(search.toLowerCase());
    const matchesPlan = planFilter === 'all' || org.plan === planFilter;
    return matchesSearch && matchesPlan;
  });

  const formatNumber = (n: number) => n.toLocaleString();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Platform Admin
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Manage all organizations on LogoPulse
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700}>{orgs.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total Orgs</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700}>
              {orgs.filter((o) => o.plan !== 'free').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Paid</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700}>
              {formatNumber(orgs.reduce((sum, o) => sum + o.activeAccounts, 0))}
            </Typography>
            <Typography variant="body2" color="text.secondary">Total Accounts</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700}>
              {formatNumber(orgs.reduce((sum, o) => sum + o.eventsThisMonth, 0))}
            </Typography>
            <Typography variant="body2" color="text.secondary">Events This Month</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search orgs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={planFilter}
              label="Plan"
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <MenuItem value="all">All Plans</MenuItem>
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="starter">Starter</MenuItem>
              <MenuItem value="growth">Growth</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Org Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="right">Accounts</TableCell>
              <TableCell align="right">Events/mo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.map((org) => (
              <TableRow
                key={org.orgId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(org.orgId)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{org.orgName}</Typography>
                  <Typography variant="caption" color="text.secondary">{org.orgId}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={org.plan}
                    size="small"
                    sx={{
                      bgcolor: PLAN_COLORS[org.plan] || '#9e9e9e',
                      color: '#fff',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  {org.activeAccounts} / {formatNumber(org.accountLimit)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(org.eventsThisMonth)} / {formatNumber(org.eventLimit)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={org.status}
                    size="small"
                    variant="outlined"
                    color={org.status === 'active' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {new Date(org.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
            {filteredOrgs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No organizations found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Modal */}
      <Dialog
        open={!!selectedOrg}
        onClose={() => setSelectedOrg(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : selectedOrg && (
          <>
            <DialogTitle>
              <Typography variant="h6" fontWeight={600}>{selectedOrg.orgName}</Typography>
              <Typography variant="caption" color="text.secondary">{selectedOrg.orgId}</Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} mb={2}>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">Total Accounts</Typography>
                  <Typography variant="h6" fontWeight={600}>{selectedOrg.totalAccounts}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">Events This Month</Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatNumber(selectedOrg.usage?.eventsThisMonth || 0)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" mb={1}>Change Plan</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                >
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="growth">Growth</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" mt={1}>
                Created: {new Date(selectedOrg.createdAt).toLocaleDateString()}
              </Typography>
            </DialogContent>
            <DialogActions>
              <SecondaryButton onClick={() => setSelectedOrg(null)}>Close</SecondaryButton>
              <PrimaryButton
                onClick={handleUpdatePlan}
                disabled={saving || editPlan === (selectedOrg.billing?.plan || 'free')}
              >
                {saving ? 'Saving...' : 'Update Plan'}
              </PrimaryButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
